import React, {
  ComponentProps,
  memo,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { useBodyScrollLock, useOnDialogBlur } from '@surglogs-app/hooks'

import { useLayerManager } from '../../layout'

import { Box } from './Box'

const SPACE = 8

const getXPosition = (
  alignMenu: PopupAlignment,
  left: number,
  right: number,
) => {
  if (alignMenu === 'left') {
    return {
      left: `${left}px`,
    }
  }

  return {
    right: `${right}px`,
  }
}

const getYPosition = (alignMenu: PopupAlignment, top: number) => {
  if (alignMenu === 'top') {
    return { top: top - SPACE }
  }

  return { top: top + SPACE }
}

const StyledPopup = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'columnCount',
})<{ columnCount?: number }>`
  ${({ columnCount }) =>
    columnCount &&
    css`
      column-count: ${columnCount};
    `};
`

export type PopupAlignment = 'left' | 'right' | 'top' | 'bottom'

interface PopupProps extends ComponentProps<typeof StyledPopup> {
  onClose: () => void
  alignMenu: PopupAlignment
  wrapperRef: RefObject<HTMLButtonElement | HTMLDivElement | null>
  insidePortal?: boolean
  withBodyScrollLock?: boolean
}

const Component = memo(
  ({
    id,
    children,
    onClose,
    alignMenu,
    'aria-label': ariaLabel,
    wrapperRef,
    insidePortal,
    ...rest
  }: PopupProps) => {
    const menuRef = useRef<HTMLDivElement | null>(null)
    const { zIndex, increaseBy } = useLayerManager()
    const [{ top, left, right }, setPosition] = useState({
      top: 0,
      left: 0,
      right: 0,
    })
    const [isCalculated, setIsCalculated] = useState(false)

    useEffect(() => {
      const updatePosition = () => {
        if (wrapperRef.current) {
          const rect = wrapperRef.current.getBoundingClientRect()
          const menuHeight = menuRef.current?.scrollHeight ?? 0
          const spaceBelow = window.innerHeight - rect.bottom
          const shouldShowOnTop = spaceBelow < menuHeight

          const getTopPosition = () => {
            if (alignMenu === 'top') {
              return rect.top + window.scrollY - menuHeight
            }

            if (shouldShowOnTop) {
              return rect.top + window.scrollY - (menuHeight + rect.height / 2)
            }

            return rect.top + window.scrollY + rect.height
          }

          setPosition({
            top: getTopPosition(),
            left: rect.left + window.scrollX,
            right: window.innerWidth - rect.right - window.scrollX,
          })

          setIsCalculated(true)
        }
      }

      updatePosition()

      const observer = new MutationObserver(updatePosition)

      if (menuRef.current) {
        observer.observe(menuRef.current, { childList: true, subtree: true })
      }

      return () => {
        observer.disconnect()
      }
    }, [alignMenu, wrapperRef, isCalculated])

    useOnDialogBlur({
      id,
      callback: onClose,
    })

    const component = useMemo(() => {
      if (!isCalculated) {
        return null
      }

      return (
        <StyledPopup
          {...{
            id,
            ref: menuRef,
            role: 'menu',
            zi: zIndex + increaseBy,
            'aria-label': ariaLabel,
            position: 'absolute',
            ta: 'left',
            w: 'max-content',
            miw: 'max-content',
            bgc: 'white.100',
            bra: 'sm',
            bd: '1px solid',
            bc: 'navy.25',
            overflow: 'hidden',
            onClick: (e) => e.stopPropagation(),
            ...getYPosition(alignMenu, top),
            ...getXPosition(alignMenu, left, right),
            ...rest,
          }}>
          {children}
        </StyledPopup>
      )
    }, [
      isCalculated,
      id,
      zIndex,
      increaseBy,
      ariaLabel,
      alignMenu,
      top,
      left,
      right,
      rest,
      children,
    ])

    return createPortal(component, document.body)
  },
)

const ComponentWithBodyScrollLock = (props: PopupProps) => {
  useBodyScrollLock(
    props.wrapperRef as RefObject<HTMLButtonElement | HTMLDivElement>,
  )

  return <Component {...props} />
}

export const Popup = ({ withBodyScrollLock = true, ...props }: PopupProps) => {
  if (withBodyScrollLock) {
    return <ComponentWithBodyScrollLock {...props} />
  }

  return <Component {...props} />
}
